import { Injectable } from '@angular/core';
import Swal, { SweetAlertIcon, SweetAlertOptions, SweetAlertResult } from 'sweetalert2';
import { ReflectionService } from '../utilities/reflection.service';

@Injectable({
    providedIn: 'root'
})
export class AlertService {
    constructor(private reflection: ReflectionService) {}

    changeToastToModalLength: number = 200;
    changeModalToWideModalLength: number = 255;
    confirmButtonColor: string = '#003da5';

    private popupWithCallBack(alert: Alert, options: SweetAlertOptions) {
        const finalOptions: SweetAlertOptions = options ? options : {};
        finalOptions.html = alert.text;
        finalOptions.title = alert.title;
        finalOptions.confirmButtonColor = this.confirmButtonColor;
        if (alert.cancelButtonText) {
            finalOptions.cancelButtonText = alert.cancelButtonText;
        }
        if (alert.okButtonText) {
            finalOptions.confirmButtonText = alert.okButtonText;
        } else {
            finalOptions.confirmButtonText = 'OK';
        }
        finalOptions.allowOutsideClick = false;
        if (alert?.text?.length > this.changeModalToWideModalLength) {
            finalOptions.customClass = {
                popup: 'swal-wide-container'
            };
        }
        Swal.fire(finalOptions).then((result) => {
            if (alert.callBack) {
                alert.callBack.function.call(alert.callBack.this, result.value, alert.callBack.callBackObject);
            }
        });
    }

    successCallBack(alert: Alert) {
        this.popupWithCallBack(alert, {
            icon: 'success',
            showCancelButton: false
        });
    }

    info(alert: Alert) {
        this.popupWithCallBack(alert, {
            icon: 'info',
            showCancelButton: false
        });
    }

    warning(alert: Alert) {
        this.popupWithCallBack(alert, {
            icon: 'warning',
            showCancelButton: false
        });
    }

    confirm(alert: Alert) {
        alert.title = alert.title ? alert.title : 'Are you sure you want to take this action?';
        this.popupWithCallBack(alert, {
            icon: 'question',
            showCancelButton: true
        });
    }

    confirmNoCancel(alert: Alert) {
        alert.title = alert.title ? alert.title : 'Are you sure you want to take this action?';
        this.popupWithCallBack(alert, {
            icon: 'question',
            showCancelButton: false
        });
    }

    delete(alert: Alert) {
        alert.title = alert.title ? alert.title : 'Are you sure you want to delete this item?';
        this.popupWithCallBack(alert, {
            icon: 'warning',
            showCancelButton: true
        });
    }

    private toast(message: string, icon: SweetAlertIcon) {
        Swal.fire({
            toast: true,
            position: 'top',
            icon: icon,
            title: message,
            iconColor: icon === 'success' ? 'white' : 'black',
            customClass: {
                popup: 'ucr-toast'
            },
            showConfirmButton: false,
            timer: 7000,
            timerProgressBar: true,
            didOpen: (toast) => {
                toast.addEventListener('mouseenter', Swal.stopTimer);
                toast.addEventListener('mouseleave', Swal.resumeTimer);
            }
        });
    }

    toastWarn(message?: string) {
        message = message ? message : 'Warning';
        if (message?.length > this.changeToastToModalLength) {
            this.alert({
                title: 'Warning',
                text: message
            });
        } else {
            this.toast(message, 'warning');
        }
    }

    toastErrorObject(error?: any) {
        this.toastError(this.reflection.objectToString(error?.error));
    }

    toastWarnObject(error?: any) {
        this.toastWarn(this.reflection.objectToString(error?.error));
    }

    toastError(message?: string) {
        message = message ? message : 'We encountered an error with your request please try again!';
        if (message?.length > this.changeToastToModalLength) {
            this.error({
                title: 'Error',
                text: message
            });
        } else {
            this.toast(message, 'error');
        }
    }

    toastSuccess(message?: string) {
        message = message ? message : 'Success';
        if (message?.length > this.changeToastToModalLength) {
            this.success({
                title: 'Success',
                text: message
            });
        } else {
            this.toast(message, 'success');
        }
    }

    private popupWithOutCallBack(alert: Alert, icon: SweetAlertIcon) {
        Swal.fire({
            title: alert.title,
            html: alert.text,
            icon: icon,
            customClass: {
                popup: alert?.text?.length > this.changeModalToWideModalLength ? 'swal-wide-container' : ''
            },
            confirmButtonColor: this.confirmButtonColor
        });
    }

    success(alert: Alert) {
        alert.title = alert.title ? alert.title : 'Success';
        this.popupWithOutCallBack(alert, 'success');
    }

    studentSaved() {
        this.success({
            title: 'Petition Saved',
            text: 'The petition was successfully saved. Please note that the petition has not been submitted, to submit click on the submit button.'
        });
    }

    staffSaved() {
        this.success({
            title: 'Petition Saved',
            text: 'The petition was successfully saved. To move the petition forward, you must also submit/approve.'
        });
    }

    alert(alert: Alert) {
        alert.title = alert.title ? alert.title : 'Alert';
        this.popupWithOutCallBack(alert, 'warning');
    }

    error(alert?: Alert) {
        alert = alert ? alert : {};
        alert.title = alert.title ? alert.title : 'Error';
        alert.text = alert.error
            ? alert.error.status + ' ' + alert.error.message
            : alert && alert.text
            ? alert.text
            : 'We encountered an error please try your request again.';
        this.popupWithOutCallBack(alert, 'error');
    }

    withRequiredTextInput(title: string, text: string, label: string, confirm: string): Promise<SweetAlertResult<any>> {
        return Swal.fire({
            title: title,
            text: text,
            input: 'text',
            showCancelButton: true,
            confirmButtonText: confirm,
            confirmButtonColor: this.confirmButtonColor,
            inputLabel: label,
            inputValidator: (inputValue: string) => {
                if (!inputValue) {
                    return label + ' is required';
                }
            }
        });
    }

    close() {
        Swal.close();
    }
}

export interface Alert {
    title?: string;
    text?: string;
    error?: any;
    cancelButtonText?: string;
    okButtonText?: string;
    callBack?: {
        function: (result: boolean, obj: any) => any;
        callBackObject?: any;
        this: any;
    };
}
